import React from 'react';
import {
  faEye,
  faPaperPlane,
  faPen,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import Menu, { MenuItemOption } from 'elements/Menu/Menu';
import { useTranslations } from 'next-intl';

export type Permission = 'edit' | 'view';

type Props = {
  permission: Permission;
  onChange: (next: Permission) => void;
  onResend?: () => void;
  showResend?: boolean;
  onRemove?: () => void;
  showRemove?: boolean;

  showChevronUpWhenOpen?: boolean;
  showCanPrefix?: boolean;
  isLoading?: boolean;
};

const PermissionMenu: React.FC<Props> = ({
  permission,
  onChange,
  onResend,
  showResend = false,
  onRemove,
  showRemove = true,

  showChevronUpWhenOpen = false,
  showCanPrefix = false,
  isLoading = false,
}) => {
  const t = useTranslations();

  // "Can edit" in a member list, just "Edit" next to an email being invited.
  const permissionLabel = showCanPrefix
    ? t(
        permission === 'edit'
          ? 'boxSharing.permission.canEdit'
          : 'boxSharing.permission.canView',
      )
    : t(
        permission === 'edit'
          ? 'boxSharing.permission.edit'
          : 'boxSharing.permission.view',
      );

  const items: MenuItemOption[] = [
    {
      key: 'edit',
      label: t('boxSharing.permission.canEdit'),
      icon: faPen,
      iconBoxClassName: styles.permissionMenu.iconBox.edit,
      iconClassName: styles.permissionMenu.iconOnEdit,
      isSelected: permission === 'edit',
      onSelect: () => onChange('edit'),
    },
    {
      key: 'view',
      label: t('boxSharing.permission.canView'),
      icon: faEye,
      iconBoxClassName: styles.permissionMenu.iconBox.view,
      iconClassName: styles.permissionMenu.iconOnView,
      isSelected: permission === 'view',
      onSelect: () => onChange('view'),
      showDividerBefore: true,
    },
    ...(showResend && onResend
      ? [
          {
            key: 'resend',
            label: t('boxSharing.resend'),
            icon: faPaperPlane,
            iconBoxClassName: styles.permissionMenu.iconBox.resend,
            iconClassName: styles.permissionMenu.iconResend,
            onSelect: onResend,
            showDividerBefore: true,
          },
        ]
      : []),
    ...(showRemove && onRemove
      ? [
          {
            key: 'remove',
            label: t('common.remove'),
            icon: faXmark,
            iconBoxClassName: styles.permissionMenu.iconBox.remove,
            iconClassName: styles.permissionMenu.iconRemove,
            labelClassName: styles.permissionMenu.removeText,
            onSelect: onRemove,
            showDividerBefore: true,
          },
        ]
      : []),
  ];

  return (
    <Menu
      label={permissionLabel}
      items={items}
      isLoading={isLoading}
      showChevronUpWhenOpen={showChevronUpWhenOpen}
      buttonClassName={styles.personRight}
      labelClassName={styles.personPermission}
      chevronClassName={styles.text.muted}
      spinnerClassName={styles.spinner}
      menuClassName={styles.permissionMenu.container}
      itemButtonClassName={styles.permissionMenu.itemBtn}
      itemLeftClassName={styles.permissionMenu.left}
      itemLabelClassName={styles.permissionMenu.label}
      itemCheckClassName={styles.permissionMenu.check}
      dividerClassName={styles.permissionMenu.divider}
    />
  );
};

const styles = {
  text: {
    muted: 'text-[#6C727E]',
  },
  personRight: 'flex items-center gap-1',
  personPermission: 'text-[14px] text-[#6C727E]',
  spinner:
    'h-4 w-4 animate-spin rounded-full border-2 border-[#6C727E] border-t-transparent',

  permissionMenu: {
    container:
      'w-52 rounded-xl bg-white p-0.5 shadow-lg z-30 focus:outline-hidden',
    itemBtn:
      'flex w-full items-center justify-between rounded-lg px-2 py-2 hover:bg-[#F4F4F4]',
    left: 'flex items-center gap-2',
    label: 'text-base text-[#161E3A] font-normal leading-[27px]',
    check: 'text-[#161E3A] text-sm',
    divider: 'mr-2 ml-13 h-px bg-[#E5EAF3]',
    iconOnEdit: 'text-white text-sm',
    iconOnView: 'text-[#6C727E] text-sm',
    iconResend: 'text-[#6C727E] text-sm',
    iconRemove: 'text-[#FF0039] text-sm',
    iconBox: {
      edit: 'h-8 w-8 rounded-lg bg-mf-blue flex items-center justify-center mr-1',
      view: 'h-8 w-8 rounded-lg bg-[#F4F4F4] flex items-center justify-center mr-1',
      resend:
        'h-8 w-8 rounded-lg bg-[#F4F4F4] flex items-center justify-center mr-1',
      remove:
        'h-8 w-8 rounded-lg bg-[#FFF7F7] flex items-center justify-center mr-1',
    },
    removeText: 'text-base text-[#FF0039]',
  },
};

export default PermissionMenu;
